import { useEffect, useState } from 'react';

import { Partner, PartnerCategory, Society } from 'types';

import civilStatus from 'constants/civilStatus';
import { useTranslate } from 'hooks/useTranslate';
import { boardDelegationRoles } from 'modules/boards/constants/boardDelegation';
import { useSelector } from 'react-redux';
import { State } from 'redux/initialState';
import { hasPartnersDecoupled } from 'utils/showDetails';
import { Language } from 'constants/languages';
import i18n from 'i18n/config';

type Option = {
  label: string;
  value: string;
};

type Props = {
  currentPartner: Partner;
  actualSociety: Society;
  partnerCategories: PartnerCategory[];
  partnerLanguages: Language[];
};

export function useGetPartnerOptions({
  currentPartner,
  actualSociety,
  partnerCategories,
  partnerLanguages,
}: Props) {
  const { t } = useTranslate();
  const partners = useSelector((state: State) =>
    hasPartnersDecoupled() ? state.partners : actualSociety?.partners
  );

  const [categoryOptions, setCategoryOptions] = useState<Option[]>([]);
  const [sindicationOptions, setSindicationOptions] = useState<Option[]>([]);
  const [civilStatusOptions, setCivilStatusOptions] = useState<Option[]>([]);
  const [languageOptions, setLanguageOptions] = useState<Option[]>([]);

  useEffect(() => {
    const options = partnerCategories
      .filter((category) => !category?.isDefault)
      .map((category) => ({
        value: category['_id'],
        label: i18n.t(category.name),
      }));

    setCategoryOptions(options);
  }, [partnerCategories, i18n.language]);

  useEffect(() => {
    const options = partners
      ?.filter((partner) => partner?._id !== currentPartner?._id)
      .map((partner) => ({
        value: partner?.['_id'],
        label: `${partner?.cif} | ${partner?.name}`,
        role: boardDelegationRoles.PARTNER,
      }));

    setSindicationOptions(options);
  }, [actualSociety, currentPartner, partners]);

  useEffect(() => {
    const options = Object.entries(civilStatus).map(([, value]) => ({
      value,
      label: t(value),
    }));

    setCivilStatusOptions(options);
  }, [civilStatus]); // eslint-disable-line

  useEffect(() => {
    const options = partnerLanguages.map((language) => ({
      value: language.value,
      label: t(language.name),
    }));

    setLanguageOptions(options);
  }, [partnerLanguages]); // eslint-disable-line

  return {
    data: {
      categoryOptions,
      sindicationOptions,
      civilStatusOptions,
      languageOptions,
    },
    isLoading: false,
    isError: false,
    actions: {},
  };
}
